import { useState } from "react";
import { Empty, ListSkeleton } from "../../components/ui";
import { useMe, useReport, useTeam } from "../../lib/queries";
import type { HandoffReason, Report } from "../../lib/types";
import { money } from "../quotes/quote-text";
import { reportWorkbook } from "./report-excel";

// ---- Periods (Costa Rica days, UTC−6) -----------------------------------------------

const DAY = 86_400_000;
const crToday = () => new Date(Date.now() - 6 * 3_600_000).toISOString().slice(0, 10);
const addDays = (ymd: string, n: number) =>
  new Date(Date.parse(`${ymd}T12:00:00Z`) + n * DAY).toISOString().slice(0, 10);

type PeriodKey = "today" | "7d" | "30d" | "month" | "lastMonth" | "custom";

const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: "today", label: "Hoy" },
  { key: "7d", label: "7 días" },
  { key: "30d", label: "30 días" },
  { key: "month", label: "Este mes" },
  { key: "lastMonth", label: "Mes pasado" },
  { key: "custom", label: "Rango" },
];

function rangeOf(key: Exclude<PeriodKey, "custom">): { from: string; to: string } {
  const today = crToday();
  const firstOfMonth = `${today.slice(0, 8)}01`;
  if (key === "today") return { from: today, to: today };
  if (key === "7d") return { from: addDays(today, -6), to: today };
  if (key === "30d") return { from: addDays(today, -29), to: today };
  if (key === "month") return { from: firstOfMonth, to: today };
  const lastDay = addDays(firstOfMonth, -1);
  return { from: `${lastDay.slice(0, 8)}01`, to: lastDay };
}

const shortDate = (ymd: string) =>
  new Date(`${ymd}T12:00:00Z`).toLocaleDateString("es-CR", { day: "numeric", month: "short", timeZone: "UTC" });

// ---- Formats ------------------------------------------------------------------------

const int = (n: number) => new Intl.NumberFormat("es-CR").format(n).replace(/\s/g, " ");
const pct = (n: number | null) => (n == null ? "—" : `${Math.round(n * 100)} %`);
/** Days, or hours when it took less than one. */
function days(n: number | null) {
  if (n == null) return "—";
  if (n >= 1) return `${n.toFixed(1).replace(".", ",")} días`;
  const h = n * 24;
  return h < 1 ? "menos de 1 h" : `${Math.round(h)} h`;
}
function minutes(n: number | null) {
  if (n == null) return "—";
  if (n < 1) return "menos de 1 min";
  if (n < 60) return `${Math.round(n)} min`;
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  return m ? `${h} h ${m} min` : `${h} h`;
}
const amounts = (m: { CRC: number; USD: number }) =>
  [m.CRC && money(m.CRC, "CRC"), m.USD && money(m.USD, "USD")].filter(Boolean).join(" + ") || money(0, "CRC");

export const REASON_LABEL: Record<HandoffReason | "other", string> = {
  ai_handoff: "Lumi lo pasó a una persona",
  ai_check: "Lumi no pudo responder con seguridad",
  agent_reply: "Un asesor respondió en lugar de Lumi",
  manual: "Un asesor la tomó o la reabrió",
  off_hours: "Fuera de horario (mensaje fijo)",
  ai_limit: "Lumi llegó a su límite en la conversación",
  ai_no_balance: "Sin mensajes de Lumi disponibles",
  ai_error: "Error de la IA",
  other: "Otro motivo",
};

// ---- Page ---------------------------------------------------------------------------

/** Management reports: the period, compared with the one of the same length before it. */
export function ReportsPage() {
  const [period, setPeriod] = useState<PeriodKey>("30d");
  const [custom, setCustom] = useState(() => rangeOf("30d"));
  const range = period === "custom" ? custom : rangeOf(period);
  const report = useReport(range.from, range.to);
  const team = useTeam().data?.items ?? [];
  const me = useMe().data;
  const names = new Map(team.map((m) => [m.id, m.name]));
  const company = me?.tenants.find((t) => t.tenantId === me.activeTenantId)?.tenantName ?? "tu empresa";
  const r = report.data;
  const own = r?.scope === "own";
  const validCustom = custom.from <= custom.to;

  return (
    <div className="page reports">
      <div className="page-head">
        <h1>{own ? "Mis números" : "Reportes"}</h1>
        {r && (
          <button
            className="btn"
            type="button"
            onClick={() => download(r, range, names, company, own ? (me?.user.name ?? "") : null)}
          >
            Descargar Excel
          </button>
        )}
      </div>
      <div className="page-head reports-period">
        <fieldset className="filters">
          <legend className="sr-only">Período</legend>
          {PERIODS.map((p) => (
            <button key={p.key} type="button" aria-pressed={period === p.key} onClick={() => setPeriod(p.key)}>
              {p.label}
            </button>
          ))}
        </fieldset>
        {period === "custom" && (
          <div className="reports-range">
            <input
              className="input"
              type="date"
              aria-label="Desde"
              value={custom.from}
              max={crToday()}
              onChange={(e) => e.target.value && setCustom({ ...custom, from: e.target.value })}
            />
            <span className="faint">a</span>
            <input
              className="input"
              type="date"
              aria-label="Hasta"
              value={custom.to}
              max={crToday()}
              onChange={(e) => e.target.value && setCustom({ ...custom, to: e.target.value })}
            />
          </div>
        )}
        <span className="faint">
          {range.from === range.to ? shortDate(range.from) : `Del ${shortDate(range.from)} al ${shortDate(range.to)}`}
          {r ? ` · comparado con los ${r.days === 1 ? "el día anterior" : `${r.days} días anteriores`}` : ""}
        </span>
      </div>

      {!validCustom ? (
        <div className="banner banner-warn">La fecha de inicio va antes que la de fin.</div>
      ) : report.isError ? (
        <div className="banner banner-warn">No se pudo cargar el reporte. Prueba con un período más corto.</div>
      ) : !r ? (
        <div className="card">
          <ListSkeleton rows={4} />
        </div>
      ) : (
        <div className={`reports-body${report.isFetching ? " is-loading" : ""}`}>
          <ConversationsSection r={r} />
          <TeamSection r={r} names={names} />
          <PostSaleSection r={r} />
          <SalesSection r={r} />
        </div>
      )}
    </div>
  );
}

// ---- Pieces -------------------------------------------------------------------------

/**
 * A number with its change from the previous period. Whether up is good depends on the
 * number (more sales: good; longer waits: bad), so the arrow says direction and the
 * word says whether it is better.
 */
function Stat({
  label,
  value,
  current,
  previous,
  lowerIsBetter = false,
  hint,
}: {
  label: string;
  value: string;
  current?: number | null;
  previous?: number | null;
  lowerIsBetter?: boolean;
  hint?: string;
}) {
  let delta: { text: string; tone: "good" | "bad" | "flat" } | null = null;
  if (current != null && previous != null) {
    if (current === previous) delta = { text: "igual que antes", tone: "flat" };
    else if (previous === 0) delta = { text: "nuevo en este período", tone: "flat" };
    else {
      const change = (current - previous) / previous;
      const better = lowerIsBetter ? change < 0 : change > 0;
      delta = {
        text: `${change > 0 ? "▲" : "▼"} ${Math.abs(Math.round(change * 100))} % ${better ? "mejor" : "peor"}`,
        tone: better ? "good" : "bad",
      };
    }
  }
  return (
    <div className="stat">
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
      {delta && <span className={`stat-delta ${delta.tone}`}>{delta.text}</span>}
      {hint && <span className="faint stat-hint">{hint}</span>}
    </div>
  );
}

/** Magnitudes in one hue: the value is always written beside its bar, never color alone. */
function Bars({ items, format = int }: { items: { label: string; value: number }[]; format?: (n: number) => string }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  if (!items.length) return <p className="muted">Nada en este período.</p>;
  return (
    <ul className="bars">
      {items.map((i) => (
        <li key={i.label} title={`${i.label}: ${format(i.value)}`}>
          <span className="bars-label">{i.label}</span>
          <span className="bars-track" aria-hidden="true">
            <span className="bars-fill" style={{ width: `${Math.max(2, (i.value / max) * 100)}%` }} />
          </span>
          <span className="bars-value">{format(i.value)}</span>
        </li>
      ))}
    </ul>
  );
}

function Section({ title, intro, children }: { title: string; intro?: string; children: React.ReactNode }) {
  return (
    <section className="card reports-section">
      <div className="card-body">
        <div>
          <h3>{title}</h3>
          {intro && <p className="muted">{intro}</p>}
        </div>
        {children}
      </div>
    </section>
  );
}

const ratio = (a: number, b: number) => (b ? a / b : null);

function ConversationsSection({ r }: { r: Report }) {
  const c = r.current.conversations;
  const p = r.previous.conversations;
  const reasons = Object.entries(c.byReason)
    .map(([k, v]) => ({ label: REASON_LABEL[k as HandoffReason] ?? REASON_LABEL.other, value: v ?? 0 }))
    .sort((a, b) => b.value - a.value);
  return (
    <Section title="Conversaciones" intro={r.scope === "own" ? "Las conversaciones asignadas a ti." : undefined}>
      <div className="stats">
        <Stat label="Conversaciones nuevas" value={int(c.started)} current={c.started} previous={p.started} />
        <Stat label="Mensajes de clientes" value={int(c.inbound)} current={c.inbound} previous={p.inbound} />
        {c.lumi && p.lumi && (
          <Stat
            label="Resueltas por Lumi sola"
            value={pct(ratio(c.lumi.solvedAlone, c.lumi.answered))}
            current={ratio(c.lumi.solvedAlone, c.lumi.answered)}
            previous={ratio(p.lumi.solvedAlone, p.lumi.answered)}
            hint={`${int(c.lumi.solvedAlone)} de ${int(c.lumi.answered)} que atendió`}
          />
        )}
        <Stat label="Pasaron a una persona" value={int(c.handoffs)} current={c.handoffs} previous={p.handoffs} />
        <Stat
          label="Esperando respuesta ahora"
          value={int(r.waitingNow)}
          hint="Clientes cuyo último mensaje no tiene respuesta"
        />
        {c.lumi && p.lumi && (
          <Stat
            label="Mensajes de Lumi usados"
            value={int(c.lumi.messages)}
            current={c.lumi.messages}
            previous={p.lumi.messages}
          />
        )}
      </div>
      <h4>Por qué pasaron a una persona</h4>
      <Bars items={reasons} />
    </Section>
  );
}

function TeamSection({ r, names }: { r: Report; names: Map<string, string> }) {
  const t = r.current.team;
  const areas = t.areas ? Object.entries(t.areas).map(([label, value]) => ({ label, value })) : null;
  return (
    <Section
      title={r.scope === "own" ? "Tus respuestas" : "Equipo"}
      intro="La espera es cuánto tarda un cliente en recibir respuesta de una persona: desde su mensaje, o desde que Lumi lo pasó, hasta la respuesta."
    >
      <div className="stats">
        <Stat
          label="Espera típica (mediana)"
          value={minutes(t.medianWaitMinutes)}
          current={t.medianWaitMinutes}
          previous={r.previous.team.medianWaitMinutes}
          lowerIsBetter
        />
      </div>
      {t.agents.length ? (
        <div className="table-wrap">
          <table className="table reports-table">
            <thead>
              <tr>
                <th scope="col">Asesor</th>
                <th scope="col">Conversaciones</th>
                <th scope="col">Respuestas</th>
                <th scope="col">Espera típica</th>
                <th scope="col">Respondidas en 15 min</th>
              </tr>
            </thead>
            <tbody>
              {t.agents.map((a) => (
                <tr key={a.userId}>
                  <th scope="row">{names.get(a.userId) ?? "Persona que ya no está"}</th>
                  <td>{int(a.conversations)}</td>
                  <td>{int(a.replies)}</td>
                  <td>{minutes(a.medianWaitMinutes)}</td>
                  <td>{pct(a.within15)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty title="Sin respuestas de asesores en este período." />
      )}
      {areas && (
        <>
          <h4>Conversaciones que Lumi pasó a cada área</h4>
          <Bars items={areas.sort((a, b) => b.value - a.value)} />
        </>
      )}
    </Section>
  );
}

function PostSaleSection({ r }: { r: Report }) {
  const c = r.current.postSale.cases;
  const pc = r.previous.postSale.cases;
  const parts = r.current.postSale.parts;
  const pp = r.previous.postSale.parts;
  return (
    <Section title="Postventa">
      <div className="stats">
        <Stat label="Casos abiertos ahora" value={int(c.openNow)} />
        <Stat label="Casos nuevos" value={int(c.opened)} current={c.opened} previous={pc.opened} lowerIsBetter />
        <Stat label="Casos cerrados" value={int(c.closed)} current={c.closed} previous={pc.closed} />
        <Stat
          label="Tiempo para cerrar un caso"
          value={days(c.averageDaysToClose)}
          current={c.averageDaysToClose}
          previous={pc.averageDaysToClose}
          lowerIsBetter
        />
      </div>
      <h4>Días promedio en cada estado</h4>
      <Bars items={c.byStatus.map((s) => ({ label: s.status, value: s.averageDays }))} format={(n) => days(n)} />
      <h4>Repuestos</h4>
      <div className="stats">
        <Stat
          label="Solicitudes nuevas"
          value={int(parts.requested)}
          current={parts.requested}
          previous={pp.requested}
        />
        <Stat label="Pendientes ahora" value={int(parts.pendingNow)} />
        <Stat label="Llegaron" value={int(parts.arrived)} current={parts.arrived} previous={pp.arrived} />
        <Stat
          label="Tiempo hasta que llega"
          value={days(parts.averageDaysToArrive)}
          current={parts.averageDaysToArrive}
          previous={pp.averageDaysToArrive}
          lowerIsBetter
        />
      </div>
    </Section>
  );
}

function SalesSection({ r }: { r: Report }) {
  const s = r.current.sales;
  const p = r.previous.sales;
  return (
    <Section
      title="Ventas"
      intro="La tasa de cierre cuenta las cotizaciones aceptadas entre las que se decidieron (aceptadas o rechazadas) en el período."
    >
      <div className="stats">
        <Stat label="Cotizaciones enviadas" value={int(s.sent)} current={s.sent} previous={p.sent} />
        <Stat label="Aceptadas" value={int(s.accepted)} current={s.accepted} previous={p.accepted} />
        <Stat label="Rechazadas" value={int(s.rejected)} current={s.rejected} previous={p.rejected} lowerIsBetter />
        <Stat label="Tasa de cierre" value={pct(s.closeRate)} current={s.closeRate} previous={p.closeRate} />
        <Stat label="Monto aceptado" value={amounts(s.acceptedAmount)} hint={`Enviado: ${amounts(s.sentAmount)}`} />
        <Stat label="Esperando respuesta del cliente" value={int(s.openNow)} />
      </div>
      <h4>Lo más cotizado</h4>
      <Bars
        items={s.topProducts.map((t) => ({ label: t.label, value: t.quotes }))}
        format={(n) => `${int(n)} cotizaciones`}
      />
    </Section>
  );
}

// ---- Excel ----------------------------------------------------------------------------

function download(
  r: Report,
  range: { from: string; to: string },
  names: Map<string, string>,
  company: string,
  own: string | null,
) {
  const period =
    range.from === range.to ? shortDate(range.from) : `Del ${shortDate(range.from)} al ${shortDate(range.to)}`;
  const blob = reportWorkbook(r, { company, period, range, names, own, reasons: REASON_LABEL });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `Reporte-Orbis-${range.from}-a-${range.to}.xlsx`;
  a.click();
  URL.revokeObjectURL(a.href);
}
